// Expert profile: who they are in their own credentialing terms, the
// contested record in the open, their practices, where they publish, what
// they disclose. Outbound links are allowed here.
import { useEffect, useState } from "react";
import type { Descriptor } from "../shell/descriptor.js";
import { Avatar } from "../shell/Avatar.js";
import { EvChip, MetaLine, RailCard, useExpert } from "../shell/fragments.js";
import { artFor } from "../shell/placeholderArt.js";
import { useStore, type AppStore } from "../state/store.js";
import { artCss, artImg } from "../assets.js";

function Loader({ id }: { id: string }) {
  const loadExpertDetail = useStore((s) => s.loadExpertDetail);
  const crossfire = useStore((s) => s.crossfire);
  const loadCrossfire = useStore((s) => s.loadCrossfire);
  useEffect(() => {
    void loadExpertDetail(id);
    if (!crossfire) void loadCrossfire();
  }, [id]);
  return null;
}

function ExpertBody({ id }: { id: string }) {
  const s = useStore();
  const lite = useExpert(id);
  const detail = s.expertDetails[id];
  const following = s.appState?.follows.includes(id) ?? false;
  const e = detail?.expert;

  const mine = detail?.cards ?? [];
  const topicOf = (tid: string | null) => s.appState?.topics.find((t) => t.id === tid);
  const muted = (tid: string | null) =>
    Boolean(tid && s.appState?.mutes.some((m) => m.expertId === id && m.topicId === tid));

  return (
    <section className="panel glass">
      <Loader id={id} />
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div>
          <Avatar e={lite} size="l" />
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 26, fontWeight: 600, lineHeight: 1.15 }}>
            {lite.name}
          </h1>
          <p style={{ fontSize: 13, color: "var(--t-mid)", marginTop: 4 }}>
            {lite.role ?? ""}
            {lite.lens ? ` · ${lite.lens}` : ""}
          </p>
          {/* The no-affiliation line used to sit here, for counsel's launch
              note 11: a page built from someone's name, face and words has to
              say on the page that they are a subject and not a partner.
              Removed 2 Aug 2026 (Rob) because the footer now says it. The
              footer disclosure gained "or Lab 10" that morning, which was the
              only thing this sentence said that it did not, and the footer is
              on this screen and every other one. The note is satisfied, by a
              line the reader meets once instead of twice. */}
        </div>
        <button className={`btn ${following ? "" : "solid"}`} onClick={() => s.toggleFollow(id)}>
          {following ? "Following ✓" : "Follow"}
        </button>
      </div>
      {e?.bio && (
        <p
          className="sect serif"
          style={{ fontFamily: "var(--serif)", fontSize: 15, lineHeight: 1.6, color: "var(--t-hi)" }}
        >
          {e.bio}
        </p>
      )}

      {e?.riskNote && (
        <div className="notice guard" style={{ marginTop: 16 }}>
          <span className="ni">△</span>
          <span>{String(e.riskNote)}</span>
        </div>
      )}

      {/* Disclosures moved out of the rail on 2 Aug 2026 (Rob): a person with
          six of them made the third column run a screen and a half past the
          profile beside it. They read here anyway, between the risk note and
          the practices: what someone sells is context for what they tell you
          to do, and in the rail it sat below the fold on exactly the profiles
          that had the most of it. */}
      <Disclosures id={id} />

      {following && <MutePanel expertId={id} />}

      {/* Programs stay parked: an entry point on a sixth of the profiles
          reads as a broken promise on the rest (spec section 8). Report
          issue lives in the rail's commitment card, not here (28 Jul 2026). */}
      {mine.length > 0 && (
        <div className="sect">
          <span className="u">
            Practices from {lite.short ?? lite.name} · {mine.length}
          </span>
          <div className="grid2" style={{ marginTop: 10 }}>
            {mine.map((c) => {
              const t = topicOf(c.topicId);
              const on = Boolean(t?.on) && !muted(c.topicId);
              const added = s.appState?.plan.some((p) => p.cardId === c.id && p.status !== "stopped");
              return (
                // The whole card opens the practice, same as Discover's cards
                // (29 Jul 2026): the metadata line and the art around the
                // title read as dead space otherwise. Inner controls stop
                // propagation; the title stays a real button for the keyboard.
                <div
                  className="sug imgbg"
                  key={c.id}
                  onClick={() => s.openSheet({ kind: "practice", cardId: c.id })}
                  style={{ ["--photo" as string]: artCss(artFor(c)) }}
                >
                  <MetaLine parts={[<EvChip key="e" chip={c.chip} />, t?.short]} />
                  <button className="do">{c.do}</button>
                  {added ? (
                    // A state, not a control: text in the accent, no capsule
                    // (29 Jul 2026). A pill here invited a click that did
                    // nothing.
                    <span className="statetx">In your practice ✓</span>
                  ) : on ? (
                    <div className="acts2">
                      {/* Add opens the practice modal: version choice and
                          the committing Add live there, so a separate
                          Details button would open the same thing twice. */}
                      <button
                        className="btn solid"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          s.openSheet({ kind: "practice", cardId: c.id });
                        }}
                      >
                        Add
                      </button>
                    </div>
                  ) : (
                    <span style={{ fontSize: 12, color: "var(--t-low)" }}>
                      {t?.short ?? "This topic"} is filtered in your{" "}
                      <button
                        className="txt"
                        style={{ textDecoration: "underline", fontSize: 12 }}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          s.nav("controls");
                        }}
                      >
                        settings
                      </button>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

/* What they have said they are paid for, or hold a stake in. One overline
   rather than the rail card's eyebrow-plus-title, which is how every other
   block in this column is headed; the two lines it used to carry are said in
   the one. */
function Disclosures({ id }: { id: string }) {
  const detail = useStore((s) => s.expertDetails[id]);
  if (!detail || detail.disclosures.length === 0) return null;
  return (
    <div className="sect">
      <span className="u">Disclosures, stated up front</span>
      {detail.disclosures.map((d) => (
        <p key={d.id} style={{ marginBottom: 9 }}>
          <b>{d.label}.</b> {d.detail ?? ""}
        </p>
      ))}
    </div>
  );
}

// The per-expert switches, on the profile since 28 Jul 2026 (the
// standalone what-reaches-you screen is gone). Only rendered while
// following: before that there is nothing reaching you to tune.
function MutePanel({ expertId }: { expertId: string }) {
  const s = useStore();
  const lite = useExpert(expertId);
  const detail = s.expertDetails[expertId];
  const short = lite.short ?? lite.name;
  const topicIds = [
    ...new Set((detail?.cards ?? []).map((c) => c.topicId).filter((t): t is string => Boolean(t))),
  ];
  if (!topicIds.length) return null;
  return (
    <div className="sect">
      <span className="u">What reaches you from {short}</span>
      <p className="lede-sm" style={{ marginTop: 8 }}>
        Applies to {short} only. Muting a topic here keeps it available from everyone else.
      </p>
      <div style={{ marginTop: 4 }}>
        {topicIds.map((tid) => {
          const t = s.appState?.topics.find((x) => x.id === tid);
          if (!t) return null;
          const muted = s.appState?.mutes.some((m) => m.expertId === expertId && m.topicId === tid) ?? false;
          const globalOff = !t.on;
          return (
            <div className="swrow" key={tid}>
              <span className="grow">
                <strong>{t.label}</strong>
                {globalOff ? (
                  <p>
                    Filtered everywhere.{" "}
                    <button className="txt" style={{ textDecoration: "underline" }} onClick={() => s.nav("controls")}>
                      Topic controls ›
                    </button>
                  </p>
                ) : muted ? (
                  <p>
                    Muted from {short}. You’ll still see {t.label.toLowerCase()} from everyone else you follow.
                  </p>
                ) : null}
              </span>
              <button
                className="sw"
                role="switch"
                aria-checked={!muted && !globalOff}
                aria-label={`${t.label} from ${short}`}
                disabled={globalOff}
                style={globalOff ? { opacity: 0.35, cursor: "default" } : undefined}
                onClick={() => void s.setMute(expertId, tid, !muted)}
              />
            </div>
          );
        })}
      </div>
      <div className="notice safe" style={{ marginTop: 10 }}>
        <span className="ni">◇</span>
        <span>
          <b>This is your call, not ours.</b> We don’t decide that {short}’s views on a topic are unfit for you. We label them, disclose what’s commercial, and give you the switch.
        </span>
      </div>
    </div>
  );
}

// Three recent items from this expert, cached per id for the session.
// Served by the same open feed endpoint the picker uses: followed or not,
// a feed is always viewable.
const expertFeedCache = new Map<
  string,
  Array<{ id: string; title: string; kind: string | null; date: string | null; image: string | null }>
>();

function useExpertFeed(id: string) {
  const [items, setItems] = useState(expertFeedCache.get(id) ?? null);
  useEffect(() => {
    if (expertFeedCache.has(id)) {
      setItems(expertFeedCache.get(id) ?? null);
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const res = await fetch(`/api/feed?expert=${encodeURIComponent(id)}&limit=3`);
        if (!res.ok) return;
        const body = (await res.json()) as { items: typeof items };
        expertFeedCache.set(id, body.items ?? []);
        if (alive) setItems(body.items ?? []);
      } catch {
        // The rail simply goes without the card.
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);
  return items;
}

function WorthALook({ id }: { id: string }) {
  const s = useStore();
  const items = useExpertFeed(id);
  if (!items || items.length === 0) return null;
  return (
    <RailCard eyebrow="Recent" title="Worth a look">
      {items.slice(0, 3).map((it) => (
        <button
          key={it.id}
          className="lrow"
          onClick={() => {
            s.setFeedExpert(id);
            s.nav("feed", it.id);
          }}
        >
          {it.image && <img className="uimg" alt="" loading="lazy" sizes="64px" {...artImg(it.image)} />}
          <span className="grow">
            <strong>{it.title}</strong>
            <small>
              {it.kind ?? "Source"}
              {it.date ? ` · ${it.date}` : ""}
            </small>
          </span>
          <span className="chev">›</span>
        </button>
      ))}
      <button
        className="btn blockbtn"
        onClick={() => {
          s.setFeedExpert(id);
          s.nav("feed");
        }}
      >
        See more in Feed ›
      </button>
    </RailCard>
  );
}

function ExpertRail({ id }: { id: string }) {
  const s = useStore();
  const detail = s.expertDetails[id];
  const inXf = (s.crossfire ?? []).filter((q) => q.lanes.some((l) => l.expertId === id));
  // The list now spans every question, not just the followed ones, and a
  // heavily contested expert can be in a dozen: five rows, then a reveal.
  const [showAllXf, setShowAllXf] = useState(false);
  const shownXf = showAllXf ? inXf : inXf.slice(0, 5);
  if (!detail) return null;
  return (
    <>
      <WorthALook id={id} />
      <RailCard eyebrow="Channels" title="Where they publish">
        {detail.channels.length ? (
          detail.channels.map((ch) =>
            ch.url ? (
              <a key={ch.id} className="mrow" style={{ textDecoration: "none" }} href={ch.url} target="_blank" rel="noopener noreferrer">
                <span className="grow">
                  <strong>{ch.label} ↗</strong>
                  <small>
                    {ch.kind}
                    {ch.note ? ` · ${ch.note}` : ""}
                  </small>
                </span>
              </a>
            ) : null,
          )
        ) : (
          <p>No public channels on file.</p>
        )}
      </RailCard>
      {/* Disclosures used to sit here; they are in the main column now (see
          Disclosures above). */}
      {inXf.length > 0 && (
        <RailCard eyebrow="In Crossfire" title="Open questions">
          {shownXf.map((q) => (
            <button key={q.id} className="mrow" onClick={() => s.nav("crossfire", q.id)}>
              <span className="grow">
                <strong>{q.question}</strong>
                <small>
                  {q.status === "decided" ? "you decided this one" : q.status === "undecided" ? "still deciding" : "open"}
                  {q.recheckDate ? ` · re-ask ${q.recheckDate}` : ""}
                </small>
              </span>
              <span className="chev">›</span>
            </button>
          ))}
          {!showAllXf && inXf.length > 5 && (
            <button className="txt" style={{ padding: "8px 8px 0" }} onClick={() => setShowAllXf(true)}>
              Show {inXf.length - 5} more ›
            </button>
          )}
        </RailCard>
      )}
      <RailCard eyebrow="Our commitment" title="Accuracy is the bar">
        <p>
          Everything on this profile traces to what they actually published, graded and linked to
          its source. A practice is excluded only for fabrication, misattribution, or
          misrepresenting its source, never for viewpoint.
        </p>
        <p style={{ marginTop: 8 }}>
          If something here looks wrong, thank you for telling us. A person reads every report,
          and any change it leads to is versioned and visible.
        </p>
        {/* The same flag as the practice modal's report control: one glyph
            for reporting on every surface (29 Jul 2026). */}
        <button
          className="btn"
          aria-label="Report issue"
          title="Report issue"
          onClick={() => s.openSheet({ kind: "report", subjectType: "expert", subjectId: id })}
        >
          ⚑ Report
        </button>
      </RailCard>
    </>
  );
}

export function expert(state: AppStore, arg: string | null): Descriptor {
  const id = arg ?? "";
  const lite = state.experts?.find((e) => e.id === id);
  return {
    nav: "follow",
    back: "follow",
    title: lite?.name ?? "Profile",
    hideHead: true,
    main: <ExpertBody id={id} />,
    rail: <ExpertRail id={id} />,
  };
}
